import React, { useEffect } from 'react';

const BarRow = ({ brand, score }) => {
  const name =
    brand === "ilmium" ? "Ilmium Agent" :
    brand === "manus"  ? "Manus.ai" :
                         "OpenAI Deep Search";

  return (
    <div className="bar-line">
      <div className="progress" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0">
        <div
          className={`progress-fill ${brand}`}
          data-bar={String(score)}
        >
          <span className="bar-name">{name}</span>
          <span className="bar-tip">{`${score}%`}</span>
        </div>
      </div>
    </div>
  );
};

const LevelBlock = ({ label, scores }) => (
  <div className="bench-level">
    <div className="level-label">{label}</div>
    <div className="level-bars">
      <BarRow brand="ilmium" score={scores.ilmium} />
      <BarRow brand="manus" score={scores.manus} />
      <BarRow brand="openai" score={scores.openai} />
    </div>
  </div>
);

const Agent = () => {
  const GAIA_LEVELS = [
    { label: "Level 1", scores: { ilmium: 88,   manus: 86.5, openai: 74.3 } },
    { label: "Level 2", scores: { ilmium: 76,   manus: 70.1, openai: 69.1 } },
    { label: "Level 3", scores: { ilmium: 65,   manus: 59,   openai: 52   } },
  ];

  const caps = [
    { label: "Multi-tool Orchestration", svg: `<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path fill="currentColor" d="M8 3h8a2 2 0 0 1 2 2v3h-2V5H8v3H6V5a2 2 0 0 1 2-2Zm10 13v3a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2v-3h2v3h8v-3h2ZM3 8h6v2H8v4h2v2H3v-2h2v-4H3V8Zm12 0h6v2h-2v4h2v2h-6v-2h2v-4h-2V8Z"/></svg>` },
    { label: "Goal-Conditioned Planning", svg: `<svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" d="M12 2a10 10 0 1 0 10 10h-2A8 8 0 1 1 12 4V2Zm0 4a6 6 0 1 0 6 6h-2a4 4 0 1 1-4-4V6Zm1 1v4h4v-2h-2V7h-2Z"/></svg>` },
    { label: "RAG + Memory", svg: `<svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" d="M4 6a2 2 0 0 1 2-2h8l6 6v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6Zm8-1v5h5l-5-5Zm-6 8h12v2H6v-2Zm0 3h8v2H6v-2Z"/></svg>` },
    { label: "Self-Evaluation & Recovery", svg: `<svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" d="M12 5a7 7 0 1 0 6.32 4H21l-3.5-3.5L14 9h2.1A5 5 0 1 1 7 12H5a7 7 0 0 0 7 7c3.87 0 7-3.13 7-7s-3.13-7-7-7Z"/></svg>` },
    { label: "Structured Outputs & Function Calls", svg: `<svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" d="M7 4h10v4H7V4Zm-2 6h8v4H5v-4Zm10 0h6v10h-6V10Zm-10 6h8v4H5v-4Z"/></svg>` },
    { label: "Human-in-the-Loop", svg: `<svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm0 2c-3.33 0-6 1.34-6 4v2h12v-2c0-2.66-2.67-4-6-4Zm7.5-6.5-2.2 2.2-1.3-1.3-1.4 1.4 2.7 2.7 3.6-3.6-1.4-1.4Z"/></svg>` },
    { label: "Multi-Agent System", svg: `<svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" d="M7 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm0 2c-3 0-5 1.6-5 4v2h6.5a6.5 6.5 0 0 1 10.49-5.12A5 5 0 0 0 12 10h-1a5 5 0 0 0-4 2ZM17 7a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"/></svg>` },
    { label: "Deterministic Fallbacks", svg: `<svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" d="M12 3a9 9 0 1 0 9 9h-2a7 7 0 1 1-7-7V3Zm1 3v5h5V9h-3V6h-2Z"/></svg>` },
  ];

  const CapBox = ({ label, svg }) => (
    <div className="cap-box tilt" role="listitem">
      <div className="cap-box-content">
        <div className="cap-icon" dangerouslySetInnerHTML={{ __html: svg }} />
        <span className="cap-label">{label}</span>
      </div>
    </div>
  );

  const CapsGrid3x3 = () => {
    const gridChildren = [];
    let iCap = 0;
    
  for (let i = 0; i < 9; i++) {
    if (i === 4) {
      gridChildren.push(
        <div key="center" className="cap-box cap-center" role="presentation" tabIndex="0">
          <div className="cap-center-inner">
            <span className="cap-center-kicker">Core</span>
            <span className="cap-center-title">Ilmium Agent</span>
          </div>
        </div>
        );
      } else {
        gridChildren.push(
          <CapBox
            key={iCap}
            label={caps[iCap].label}
            svg={caps[iCap].svg}
          />
        );
        iCap++;
      }
    }
    
    return (
      <div className="caps-grid-3x3" role="list">
        {gridChildren}
      </div>
    );
  };

  useEffect(() => {
    const animateBars = () => {
      document.querySelectorAll("#agent .progress").forEach((wrap) => {
        const fill = wrap.querySelector(".progress-fill");
        const tip = wrap.querySelector(".bar-tip");
        if (!fill) {
          return;
        }

        const target = Number((fill.dataset.bar || "0").trim());
        fill.style.width = "0%";
        if (tip) {
          tip.textContent = "0%";
        }
        wrap.setAttribute("aria-valuenow", "0");

        requestAnimationFrame(() => {
          fill.style.width = `${target}%`;

          const start = performance.now();
          const duration = 900;
          const update = (timestamp) => {
            const progress = Math.min((timestamp - start) / duration, 1);
            const value = (target * progress).toFixed(target % 1 !== 0 ? 1 : 0);
            if (tip) {
              tip.textContent = `${value}%`;
            }
            wrap.setAttribute("aria-valuenow", value);
            if (progress < 1) {
              requestAnimationFrame(update);
            }
          };

          requestAnimationFrame(update);
        });
      });
    };

    const gaiaBlock = document.querySelector("#agent .gaia");
    if (!gaiaBlock) {
      return;
    }

    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              animateBars();
              observer.disconnect();
            }
          });
        },
        { threshold: 0.5 }
      );

      observer.observe(gaiaBlock);

      return () => {
        observer.disconnect();
      };
    }

    animateBars();
  }, []);

  return (
    <section id="agent" className="agent">
      <div className="container agent-wrap">
        <div className="agent-header">
          <h2 className="section-title">Ilmium Agent</h2>
          <p className="section-sub">
            Our production-ready agent framework focuses on reliability and measurable impact.
          </p>
        </div>

        <div className="agent-panels">
          <div className="glass card gaia-card agent-card">
            <h3 className="agent-title">GAIA Phase Scores</h3>
            <div className="gaia bench">
              {GAIA_LEVELS.map((level, index) => (
                <LevelBlock
                  key={index}
                  label={level.label}
                  scores={level.scores}
                />
              ))}
            </div>
            <small className="agent-note agent-note-inline">
              * Figures shown are representative from controlled internal tests; request a live demo for your domain.
            </small>
          </div>

          <div className="glass card cap-card agent-card">
            <h3 className="agent-title">Key Capabilities</h3>
            <CapsGrid3x3 />
            <div className="cta agent-cta agent-cta-inline agent-cta-within">
              <a className="btn primary" href="#contact" id="seeAgentBtn">
                See the agent in action <span className="arrow" aria-hidden="true">→</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Agent;
